// Traffic-source attribution: where did this visitor come from?
//
// Captured once per full page load (App.js top level, next to the ?ref= capture) and kept in
// localStorage so it survives the sign-up round-trip. The server stamps it onto the new account
// (once, first touch) and onto every application, and counts logged-out Apply taps by it — the
// admin Marketing tab then shows sign-ups / apply taps / applications per source per week.
//
// First touch wins for 30 days, except that a real source (ad, share link, PESO…) replaces a
// stored "direct". The Facebook click id (?fbclid=) is kept separately — always the LATEST
// click, 90 days like Meta's own _fbc cookie — and is only handed to Meta when the visitor has
// not declined analytics (see pixel.js metaIds()).

const ATTRIBUTION_KEY = 'bt_attribution';
const FBC_KEY = 'bt_fbc';
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const FBC_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const TOKEN_RE = /[^a-z0-9_.-]/g;

function cleanToken(value) {
  const v = String(value || '').trim().toLowerCase().replace(TOKEN_RE, '').slice(0, 40);
  return v || null;
}

function cleanCampaign(value) {
  // eslint-disable-next-line no-control-regex
  const v = String(value || '').replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 100);
  return v || null;
}

function referrerSource(referrer, ownHost) {
  let host;
  let protocol;
  try {
    const u = new URL(referrer);
    host = u.hostname.toLowerCase();
    protocol = u.protocol;
  } catch (e) {
    return null;
  }
  if (protocol === 'android-app:') { // an Android app opened the link, e.g. com.google.android.gm (Gmail)
    if (/facebook|messenger|com\.facebook/.test(host)) return { source: 'facebook', medium: 'app' };
    return { source: cleanToken(host), medium: 'app' };
  }
  if (!host || host === ownHost || host.endsWith('boholtrabaho.com')) return null;
  // Round-trips through sign-in / payment providers are not where the visitor came from.
  if (/(^|\.)(supabase\.co|paymongo\.com)$/.test(host) || /^accounts\.google\./.test(host)) return null;
  const bare = host.replace(/^www\./, '');
  if (/(^|\.)google\./.test(bare)) return { source: 'google', medium: 'organic' };
  if (/(^|\.)(facebook\.com|fb\.com|fb\.me|messenger\.com)$/.test(bare)) return { source: 'facebook', medium: 'referral' };
  if (/(^|\.)instagram\.com$/.test(bare)) return { source: 'instagram', medium: 'referral' };
  if (/(^|\.)bing\.com$/.test(bare)) return { source: 'bing', medium: 'organic' };
  return { source: cleanToken(bare), medium: 'referral' };
}

// Pure: what does this landing say about where the visitor came from? null = nothing new
// (an internal navigation / reload). { source: 'direct' } = a landing with no signal at all.
export function classifyLanding(search, referrer, ownHost, pathname) {
  if (pathname && pathname.startsWith('/auth/callback')) return null; // sign-in return, not a landing
  const params = new URLSearchParams(search || '');
  const utmSource = cleanToken(params.get('utm_source'));
  if (utmSource) {
    return {
      source: utmSource,
      medium: cleanToken(params.get('utm_medium')),
      campaign: cleanCampaign(params.get('utm_campaign')),
    };
  }
  // ?peso=1 is the job list's own PESO filter chip (Jobs.js) — only a NAMED office link counts.
  const peso = params.get('peso');
  if (peso && peso !== '1') {
    return { source: 'peso', medium: 'link', campaign: cleanCampaign(peso) };
  }
  if (params.get('ref')) return { source: 'referral', medium: 'link', campaign: null };
  // Meta adds fbclid to ad clicks AND to links in ordinary posts, so this alone can't say "paid".
  if (params.get('fbclid')) return { source: 'facebook', medium: 'click', campaign: null };
  const fromReferrer = referrer ? referrerSource(referrer, ownHost) : null;
  if (fromReferrer && fromReferrer.source) return { ...fromReferrer, campaign: null };
  if (referrer) return null; // our own site or a sign-in/payment round-trip — not a new landing
  return { source: 'direct', medium: null, campaign: null };
}

function readStored(key, ttl) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object' || !v.at || Date.now() - v.at > ttl) return null;
    return v;
  } catch (e) {
    return null;
  }
}

export function captureAttribution(loc, referrer) {
  try {
    const search = loc ? loc.search : '';
    const touch = classifyLanding(search, referrer, loc ? loc.hostname : '', loc ? loc.pathname : '');
    if (touch) {
      const stored = readStored(ATTRIBUTION_KEY, ATTRIBUTION_TTL_MS);
      if (!stored || (stored.source === 'direct' && touch.source !== 'direct')) {
        localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({ ...touch, at: Date.now() }));
      }
    }
    const fbclid = new URLSearchParams(search || '').get('fbclid');
    if (fbclid && /^[A-Za-z0-9_-]{8,480}$/.test(fbclid)) {
      localStorage.setItem(FBC_KEY, JSON.stringify({ fbc: `fb.1.${Date.now()}.${fbclid}`, at: Date.now() }));
    }
  } catch (e) {
    /* attribution must never break page load */
  }
}

// { source, medium, campaign } for the server, or undefined when nothing was captured.
export function getAttribution() {
  const v = readStored(ATTRIBUTION_KEY, ATTRIBUTION_TTL_MS);
  if (!v || !v.source) return undefined;
  return { source: v.source, medium: v.medium || null, campaign: v.campaign || null };
}

// The stored Facebook click id in Meta's fbc format (fb.1.<ms>.<fbclid>), or undefined.
export function getStoredFbc() {
  const v = readStored(FBC_KEY, FBC_TTL_MS);
  return v && typeof v.fbc === 'string' ? v.fbc : undefined;
}
